import moment from 'moment';

const normalizeRange = (range) => ({
  start: moment(range.start),
  end: moment(range.end),
});

const getFilterRange = (store) => store.getters['filter/date'];

const resolveInterval = (store, { interval, allowMinute = true, forceInterval } = {}) => {
  let resolved = interval ?? store.getters['filter/interval'];

  if (forceInterval) {
    resolved = forceInterval;
  } else if (!allowMinute && resolved === 'minute') {
    resolved = 'hour';
  }

  if (resolved !== store.getters['filter/interval']) {
    store.commit('filter/SET_INTERVAL', resolved);
  }

  return resolved;
};

export const initAssetSummary = async (store, options = {}) => {
  const {
    dateRange,
    setFilterDate = false,
    interval,
    allowMinute = true,
    forceInterval,
    withDevices = false,
    withLastData = false,
  } = options;

  console.log('[initAssetSummary] Called with options:', {
    dateRange: dateRange ? {
      start: dateRange.start.format('YYYY-MM-DD HH:mm:ss'),
      end: dateRange.end.format('YYYY-MM-DD HH:mm:ss'),
    } : null,
    massId: store.getters['filter/currentMassId'],
  });

  let assets = store.getters['asset/assets'] || [];
  let loadedAssets = false;

  // Always load assets first if empty or if we need to ensure fresh data
  if (assets.length === 0) {
    console.log('[initAssetSummary] Assets empty, loading from API...');
    await store.dispatch('asset/getAssetsData');
    assets = store.getters['asset/assets'] || [];
    loadedAssets = true;
    console.log('[initAssetSummary] Assets loaded:', assets.length, 'assets');
  } else {
    console.log('[initAssetSummary] Assets already available:', assets.length, 'assets');
  }

  if (withDevices) {
    const needsDevices = assets.some((asset) => !Array.isArray(asset.devices));
    if (loadedAssets || needsDevices) {
      console.log('[initAssetSummary] Loading devices...');
      await store.dispatch('asset/getAssetsDevices');
      assets = store.getters['asset/assets'] || [];
    }
  }

  if (withLastData && loadedAssets) {
    console.log('[initAssetSummary] Loading last data...');
    await store.dispatch('asset/getLastData');
    assets = store.getters['asset/assets'] || [];
  }

  let currentMassId = store.getters['filter/currentMassId'];
  if (!currentMassId && assets.length > 0) {
    console.log('[initAssetSummary] No currentMassId, setting to first asset');
    await store.dispatch('filter/setCurrentMassId', assets[0].massId);
    currentMassId = store.getters['filter/currentMassId'];
  }

  console.log('[initAssetSummary] currentMassId:', currentMassId);
  console.log('[initAssetSummary] Available asset IDs:', assets.map((a) => a.massId));

  const resolvedInterval = resolveInterval(store, { interval, allowMinute, forceInterval });
  const resolvedRange = dateRange ? normalizeRange(dateRange) : getFilterRange(store);

  console.log('[initAssetSummary] resolvedRange:', {
    start: resolvedRange.start.format('YYYY-MM-DD HH:mm:ss'),
    end: resolvedRange.end.format('YYYY-MM-DD HH:mm:ss'),
    interval: resolvedInterval,
  });

  if (setFilterDate && dateRange) {
    store.commit('filter/SET_DATE', resolvedRange);
  }

  if (currentMassId) {
    // Verify asset exists before fetching summary
    // Convert to plain array to avoid Vue Observable issues
    const assetsArray = Array.isArray(assets) ? assets : Object.values(assets);
    const currentMassIdStr = String(currentMassId);
    console.log('[initAssetSummary] Checking asset existence:', {
      currentMassId,
      currentMassIdStr,
      assetsArrayLength: assetsArray.length,
      assetsArrayJSON: JSON.stringify(assetsArray.map((a) => ({ massId: a.massId, massName: a.massName }))),
    });
    const assetExists = assetsArray.some((asset) => {
      const match = String(asset.massId) === currentMassIdStr;
      console.log('[initAssetSummary] Checking asset:', {
        assetMassId: asset.massId, currentMassId, match,
      });
      return match;
    });
    if (!assetExists) {
      console.error(`[initAssetSummary] Asset with massId ${currentMassId} not found in loaded assets!`);
      console.log('[initAssetSummary] Available assets:', JSON.stringify(assetsArray.map((a) => a.massId)));
      return {
        assets: store.getters['asset/assets'] || [],
        currentMassId,
        interval: resolvedInterval,
        dateRange: resolvedRange,
      };
    }

    console.log('[initAssetSummary] Asset found, fetching summary for massId:', currentMassId);
    await store.dispatch('asset/fetchSummary', {
      start: resolvedRange.start,
      end: resolvedRange.end,
      interval: resolvedInterval,
      massId: currentMassId,
    });
    console.log('[initAssetSummary] Summary fetch completed');
  } else {
    console.warn('[initAssetSummary] No massId, skipping fetchSummary');
  }

  return {
    assets: store.getters['asset/assets'] || [],
    currentMassId,
    interval: resolvedInterval,
    dateRange: resolvedRange,
  };
};

export const applyAssetSummaryFilter = async (store, options = {}) => {
  console.log('====== [APPLY FILTER START] ======');
  const {
    dateRange,
    interval,
    massId,
    setFilterDate = false,
  } = options;

  console.log('[APPLY FILTER] Options:', {
    dateRangeType: dateRange ? dateRange.constructor.name : 'null',
    dateRange: dateRange ? {
      start: dateRange.start,
      startType: dateRange.start ? dateRange.start.constructor.name : 'null',
      end: dateRange.end,
      endType: dateRange.end ? dateRange.end.constructor.name : 'null',
    } : null,
    massId,
    massIdType: massId ? typeof massId : 'null',
    interval,
  });

  if (massId) {
    console.log('[APPLY FILTER] Setting massId:', massId);
    await store.dispatch('filter/setCurrentMassId', massId);
  }

  if (interval) {
    console.log('[APPLY FILTER] Setting interval:', interval);
    await store.dispatch('filter/setInterval', interval);
  }

  const resolvedMassId = massId || store.getters['filter/currentMassId'];
  const resolvedInterval = interval || store.getters['filter/interval'];
  const resolvedRange = dateRange ? normalizeRange(dateRange) : getFilterRange(store);

  console.log('[APPLY FILTER] After resolve:', {
    resolvedMassId,
    resolvedMassIdType: typeof resolvedMassId,
    resolvedInterval,
    resolvedRange: {
      start: resolvedRange.start.format('YYYY-MM-DD HH:mm:ss'),
      end: resolvedRange.end.format('YYYY-MM-DD HH:mm:ss'),
    },
  });

  if (setFilterDate && dateRange) {
    console.log('[APPLY FILTER] Setting filter date');
    store.commit('filter/SET_DATE', resolvedRange);
  }

  if (resolvedMassId) {
    // Ensure assets are loaded before fetching summary
    let assets = store.getters['asset/assets'] || [];
    console.log('[APPLY FILTER] Current assets:', {
      assetsLength: assets.length,
      assetsType: assets.constructor.name,
      assetsIds: assets.map((a) => a.massId),
    });

    if (assets.length === 0) {
      console.log('[APPLY FILTER] Assets empty, loading from API...');
      await store.dispatch('asset/getAssetsData');
      assets = store.getters['asset/assets'] || [];
      console.log('[APPLY FILTER] Assets loaded:', {
        assetsLength: assets.length,
        assetsIds: assets.map((a) => a.massId),
      });
    }

    // Verify asset exists (with string comparison)
    const assetsArray = Array.isArray(assets) ? assets : Object.values(assets);
    const resolvedMassIdStr = String(resolvedMassId);
    console.log('[APPLY FILTER] Checking asset existence:', {
      resolvedMassId,
      resolvedMassIdStr,
      assetsArrayLength: assetsArray.length,
    });

    let foundAsset = null;
    const assetExists = assetsArray.some((asset) => {
      const assetMassIdStr = String(asset.massId);
      const match = assetMassIdStr === resolvedMassIdStr;
      console.log('[APPLY FILTER] Checking asset:', {
        assetMassId: asset.massId,
        assetMassIdStr,
        assetMassName: asset.massName,
        resolvedMassIdStr,
        match,
      });
      if (match) foundAsset = asset;
      return match;
    });

    console.log('[APPLY FILTER] Asset check result:', {
      assetExists,
      foundAsset: foundAsset ? foundAsset.massName : 'null',
    });

    if (!assetExists) {
      console.error(`[APPLY FILTER] Asset with massId ${resolvedMassId} not found!`);
      console.log('====== [APPLY FILTER END - ASSET NOT FOUND] ======');
      return {
        massId: resolvedMassId,
        interval: resolvedInterval,
        dateRange: resolvedRange,
      };
    }

    console.log('[APPLY FILTER] Fetching summary for massId:', resolvedMassId);
    await store.dispatch('asset/fetchSummary', {
      start: resolvedRange.start,
      end: resolvedRange.end,
      interval: resolvedInterval,
      massId: resolvedMassId,
    });
    console.log('[APPLY FILTER] Summary fetch completed');
  }

  console.log('====== [APPLY FILTER END - SUCCESS] ======');

  return {
    massId: resolvedMassId,
    interval: resolvedInterval,
    dateRange: resolvedRange,
  };
};
